import { useEffect, useState } from "react";

/* API client for the Web Control Room → clicky-proxy worker.
   - API_BASE: fetch base. Dev goes through Vite's /api proxy; prod hits the worker.
   - API_ORIGIN: absolute worker origin, used for full-page auth redirects. */
const API_ORIGIN = (import.meta.env.VITE_API_ORIGIN as string) || "https://clicky-proxy.air.workers.dev";
const API_BASE = import.meta.env.DEV ? "/api" : API_ORIGIN;

const ACCESS_KEY = "adaptr_token";
const REFRESH_KEY = "adaptr_refresh";
export const getToken = () => localStorage.getItem(ACCESS_KEY);
export const getRefresh = () => localStorage.getItem(REFRESH_KEY);
export const isAuthed = () => !!getToken();
function setTokens(access: string, refresh?: string) {
  localStorage.setItem(ACCESS_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
}
function clearTokens() {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

/** Boot step: the WorkOS callback returns the session in the URL fragment
   (#access=…&refresh=…) so it's never sent to a server. Capture and strip it. */
export function captureSessionFromHash() {
  if (!location.hash.startsWith("#")) return;
  const params = new URLSearchParams(location.hash.slice(1));
  const access = params.get("access");
  const refresh = params.get("refresh");
  if (access) {
    setTokens(access, refresh ?? undefined);
    // Remove the fragment so tokens don't linger in the address bar / history.
    history.replaceState(null, "", location.pathname + location.search);
  }
}

/** Full-page redirect into WorkOS AuthKit. The callback returns to the SPA. */
export function login() {
  location.href = `${API_ORIGIN}/auth/login?client=web`;
}
export function logout() {
  clearTokens();
  location.href = "/";
}

export class ApiError extends Error {
  constructor(public status: number, public body: string) {
    super(`API ${status}`);
  }
}

async function rawFetch(path: string, init: RequestInit, token: string | null): Promise<Response> {
  return fetch(API_BASE + path, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers || {}),
    },
  });
}

/** Exchanges the refresh token for a fresh access token. Returns it or null. */
async function tryRefresh(): Promise<string | null> {
  const refresh = getRefresh();
  if (!refresh) return null;
  const res = await rawFetch("/auth/refresh", { method: "POST", body: JSON.stringify({ refresh }) }, null);
  if (!res.ok) return null;
  const data = (await res.json()) as { access?: string; refresh?: string };
  if (!data.access) return null;
  setTokens(data.access, data.refresh);
  return data.access;
}

/** Authenticated fetch with one transparent refresh-on-401 retry. */
export async function api<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  let res = await rawFetch(path, init, getToken());
  if (res.status === 401 && getRefresh()) {
    const fresh = await tryRefresh();
    if (fresh) res = await rawFetch(path, init, fresh);
  }
  if (!res.ok) throw new ApiError(res.status, await res.text().catch(() => ""));
  return (await res.json()) as T;
}

// ---------------------------------------------------------------------------
// Typed /me + hooks
// ---------------------------------------------------------------------------

export type Credits = { balance: number; included: number; unlimited?: boolean; resetAt?: number | null } | null;
export type MeResponse = {
  user: { id: string; email: string; role: string };
  org: { id: string; name: string; tier: string; seats?: number };
  credits: Credits;
  entitlements: Record<string, unknown> | null;
};

export type Me = {
  name: string;
  email: string;
  initials: string;
  role: string;
  plan: string;
  org: { name: string };
  credits: Credits;
  raw: MeResponse;
} | null;

const TIER_LABEL: Record<string, string> = { free: "Free", pro: "Pro", team: "Team" };
const titleize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const nameFromEmail = (email: string) =>
  (email.split("@")[0] || "")
    .split(/[._-]+/)
    .filter(Boolean)
    .map(titleize)
    .join(" ") || email;
const initialsFrom = (name: string) => {
  const parts = name.split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || name.slice(0, 2).toUpperCase();
};

function mapMe(d: MeResponse): NonNullable<Me> {
  const name = nameFromEmail(d.user.email);
  return {
    name,
    email: d.user.email,
    initials: initialsFrom(name),
    role: d.user.role,
    plan: TIER_LABEL[d.org.tier] ?? titleize(d.org.tier || "Free"),
    org: { name: d.org.name },
    credits: d.credits,
    raw: d,
  };
}

/** Start/upgrade a paid plan via Autumn → redirect to Stripe checkout if needed. */
export async function startCheckout(tier: "pro" | "team") {
  const r = await api<{ url?: string; ok?: boolean }>("/billing/checkout", {
    method: "POST",
    body: JSON.stringify({ tier }),
  });
  if (r.url) location.href = r.url;
  return r;
}
/** Clear the user's stored conversation history (Settings → Privacy & data). */
export async function clearHistory() {
  return api("/history", { method: "DELETE" });
}
/** Download a JSON bundle of everything Orbit stores for the user. */
export async function exportData() {
  const bundle = await api<unknown>("/export");
  const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `adaptr-orbit-export-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
/** Open the Stripe billing portal (manage card / invoices / cancel). */
export async function openBillingPortal() {
  const r = await api<{ url?: string }>("/billing/portal", { method: "POST" });
  if (r.url) location.href = r.url;
  return r;
}
/** Buy a one-time credit top-up of `credits` via Autumn → Stripe checkout. */
export async function buyCredits(credits: number) {
  const r = await api<{ url?: string; ok?: boolean }>("/billing/buy-credits", {
    method: "POST",
    body: JSON.stringify({ credits }),
  });
  if (r.url) location.href = r.url;
  return r;
}

let meCache: Me | undefined; // module-level so the shell + pages share one fetch

/** Loads /me once and shares it. Returns null until loaded or when signed out. */
export function useMe(): Me {
  const [me, setMe] = useState<Me>(meCache ?? null);
  useEffect(() => {
    if (meCache !== undefined) return; // already fetched this session
    if (!isAuthed()) {
      meCache = null;
      return;
    }
    let alive = true;
    api<MeResponse>("/me")
      .then((d) => {
        meCache = mapMe(d);
        if (alive) setMe(meCache);
      })
      .catch(() => {
        meCache = null;
        if (alive) setMe(null);
      });
    return () => {
      alive = false;
    };
  }, []);
  return me;
}
